import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { CheckCircle2, Search } from 'lucide-react';
import {
  AcademicYear,
  AcademicYearsApi,
  isNoCurrentYearError,
} from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatDate } from '@/lib/format';

const columnHelper = createColumnHelper<AcademicYear>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

export function AcademicYearsPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'start_date', defaultSortOrder: 'desc' });

  const { data, isFetching, error } = useQuery({
    queryKey: ['academic-years', dt.params],
    queryFn: () =>
      AcademicYearsApi.list({
        ...dt.params,
        sortBy: dt.params.sortBy as 'name' | 'start_date' | 'is_current' | undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const {
    data: currentYear,
    error: currentYearError,
    isLoading: currentYearLoading,
  } = useQuery({
    queryKey: ['academic-years', 'current'],
    queryFn: AcademicYearsApi.current,
    retry: (failureCount, err) => !isNoCurrentYearError(err) && failureCount < 3,
  });
  const noCurrentYear = isNoCurrentYearError(currentYearError);

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      enableSorting: true,
      cell: (info) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{info.getValue()}</span>
          {info.row.original.isCurrent && (
            <Badge tone="green">
              <CheckCircle2 className="-ml-0.5 mr-1 h-3 w-3" /> Current
            </Badge>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('startDate', {
      header: 'Start date',
      enableSorting: true,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('endDate', {
      header: 'End date',
      enableSorting: false,
      cell: (info) => formatDate(info.getValue()),
    }),
  ];

  return (
    <div>
      <PageHeader
        title="Academic Years"
        description={
          currentYearLoading
            ? undefined
            : noCurrentYear
              ? 'Current year: Not set'
              : currentYear
                ? `Current year: ${currentYear.name}`
                : undefined
        }
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {errMsg(error)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          result={data}
          state={dt}
          loading={isFetching}
          emptyMessage={
            dt.params.search
              ? 'No academic years match your search.'
              : 'No academic years found for this school yet.'
          }
          toolbar={
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search by name…"
                className="pl-9"
              />
            </div>
          }
        />
      )}
    </div>
  );
}
